import LButton, { ButtonProps } from "@components/LButton";
import LBox, { LBoxProps } from "@components/LBox";
import LIcon from "@components/LIcon";
import { iconColorPresets } from "@components/LIcon.layers";
import LText, { Props as LTextProps } from "@components/LText";
import { fonts } from "@theme";
import type { ReactNode } from "react";

const iconNames = {
  error: "warning",
  success: "check",
  warning: "warning",
} as const;

type BottomSheetIconType = keyof typeof iconNames;

const iconColors = {
  error: undefined,
  success: undefined,
  warning: iconColorPresets.warning.warning,
} as const;

export const BottomSheetIcon = ({
  size = 44,
  type,
}: {
  size?: number;
  type: BottomSheetIconType;
}) => {
  if (type === "warning") {
    return <LIcon name="warning" colors={iconColors.warning} size={size} />;
  }

  return <LIcon name={iconNames[type]} size={size} />;
};

export const BottomSheetTitle = (props: Omit<LTextProps, "variant">) => (
  <LText color="black" textAlign="center" variant="listItem" {...props} />
);

export const BottomSheetSubtitle = (props: Omit<LTextProps, "variant">) => (
  <LText
    color="blackTranslucent70"
    fontFamily={fonts.medium}
    fontSize={14}
    lineHeight={16}
    textAlign="center"
    {...props}
  />
);

const BottomSheetHandlePill = ({
  backgroundColor = "blackTranslucent30",
}: {
  backgroundColor?: LBoxProps["backgroundColor"];
}) => (
  <LBox
    backgroundColor={backgroundColor}
    borderRadius="max"
    height={4}
    width={48}
  />
);

export const BottomSheetHandle = ({
  backgroundColor,
}: {
  backgroundColor?: LBoxProps["backgroundColor"];
}) => (
  <LBox alignItems="center" py="s2">
    <BottomSheetHandlePill backgroundColor={backgroundColor} />
  </LBox>
);

export const BottomSheetTopBar = ({ title }: { title: ReactNode }) => (
  <LBox py="s2" alignItems="center" gap="s3">
    <BottomSheetHandlePill />
    <BottomSheetTitle>{title}</BottomSheetTitle>
  </LBox>
);

type BottomSheetLayoutProps = {
  children: ReactNode;
  contentProps?: Omit<LBoxProps, "children">;
};

export const BottomSheetTopBarLayout = ({
  children,
  contentProps,
  title,
}: BottomSheetLayoutProps & { title: ReactNode }) => (
  <LBox pb="l1">
    <BottomSheetTopBar title={title} />
    <LBox px="s4" pt="s4" {...contentProps}>
      {children}
    </LBox>
  </LBox>
);

export const BottomSheetPlainLayout = ({
  children,
  contentProps,
}: BottomSheetLayoutProps) => (
  <LBox pb="l1">
    <BottomSheetHandle />
    <LBox px="s4" pt="s4" {...contentProps}>
      {children}
    </LBox>
  </LBox>
);

export const BottomSheetSubmitButton = ({
  lPressableStyle,
  marginBottom = "s1",
  textVariant = "bodyM2",
  ...props
}: Omit<ButtonProps, "borderRadius" | "textVariant" | "variant"> &
  Partial<Pick<ButtonProps, "textVariant">>) => (
  <LButton
    borderRadius="max"
    lPressableStyle={{ width: "100%", ...lPressableStyle }}
    marginBottom={marginBottom}
    textVariant={textVariant}
    variant="roundedWhite"
    width="100%"
    {...props}
  />
);

export const BottomSheetActionLayout = ({
  actions,
  children,
  iconType,
  subtitle,
  title,
  titleProps,
}: {
  actions?: ReactNode;
  children?: ReactNode;
  iconType?: BottomSheetIconType;
  subtitle?: ReactNode;
  title: ReactNode;
  titleProps?: Omit<LTextProps, "children" | "variant">;
}) => (
  <BottomSheetPlainLayout contentProps={{ gap: "l1" }}>
    <LBox alignItems="center" gap="s3" px="l2">
      {iconType ? <BottomSheetIcon type={iconType} /> : null}
      <LBox gap="s2">
        <BottomSheetTitle {...titleProps}>{title}</BottomSheetTitle>
        {subtitle ? (
          <BottomSheetSubtitle>{subtitle}</BottomSheetSubtitle>
        ) : null}
      </LBox>
    </LBox>
    {children}
    {actions}
  </BottomSheetPlainLayout>
);
